import IconButton from '@components/v2/ui/IconButton/IconButton';
import Typography from '@components/v2/ui/Typography/Typography';
import { Box, Stack } from '@mui/material';
import { ArrowLeft } from 'lucide-react';
import { ReactNode } from 'react';
import LangEnum from '../../../../../lang/LangEnum';
import { useTranslation } from '../../../../../modules/translation/TranslationContext';
import { ScreenHeaderTagConfig } from '../screenHeader.types';
import { isBreadcrumbElement } from '../screenHeader.utils';
import { ScreenHeaderStatus } from './ScreenHeaderStatus';

const PHONE_TITLE_MAX_WIDTH = 156;

interface ScreenHeaderTitleProps {
  breadcrumb: ReactNode;
  tags?: ScreenHeaderTagConfig[];
  isPhone?: boolean;
}

export const ScreenHeaderTitle = ({ breadcrumb, tags, isPhone }: ScreenHeaderTitleProps) => {
  const { translate } = useTranslation();

  const status = <ScreenHeaderStatus tags={tags} collapse="secondary" />;

  if (!isBreadcrumbElement(breadcrumb)) {
    return (
      <Stack direction="row" sx={{ alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
        <Box sx={{ minWidth: 0, overflow: 'hidden' }}>{breadcrumb}</Box>
        {status}
      </Stack>
    );
  }

  const { items } = breadcrumb.props;
  const titleItem = items[items.length - 1];
  const parentItem = items.length > 1 ? items[items.length - 2] : undefined;
  const onBack = parentItem?.onClick;
  const title = titleItem ? translate(titleItem.label as LangEnum) : '';
  const trailing = titleItem?.trailing;

  const backButton = onBack ? (
    <IconButton
      icon={ArrowLeft}
      size="medium"
      ariaLabel={translate(LangEnum.GLOBAL_GO_BACK)}
      onClick={onBack}
    />
  ) : null;

  const titleText = (
    <Typography
      variant="h6"
      noWrap
      title={title}
      sx={{ maxWidth: isPhone ? PHONE_TITLE_MAX_WIDTH : undefined, minWidth: 0 }}
    >
      {title}
    </Typography>
  );

  if (isPhone) {
    return (
      <Stack direction="row" sx={{ alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
        {backButton}
        <Stack sx={{ gap: 0.25, minWidth: 0 }}>
          <Stack direction="row" sx={{ alignItems: 'center', gap: 0.5, minWidth: 0 }}>
            {titleText}
            {trailing}
          </Stack>
          {status}
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack direction="row" sx={{ alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
      {backButton}
      {titleText}
      {trailing}
      {status}
    </Stack>
  );
};
